import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../hooks/useAuthStore';
import { useProjectStore } from '../../hooks/useProjectStore';
import { WORKBENCH_MODULES } from '../../pages/geomechanics/modules';
import { GEOMECHANICS_UI_ENABLED } from '../../pages/geomechanics/feature';
import AIPanel from '../common/AIPanel';
import LanguageSwitch from '../common/LanguageSwitch';
import { useT } from '../../i18n';
import { filterNav, isPathAllowed } from './navScope';
import './AppLayout.css';

// group 是二级分组名 (出现即开一个新分组); steps 是工作台页内的第四级步骤 (?step=N)。
type NavChild = {
  key: string;
  label: string;
  bullet?: string;
  group?: string;
  groupLanding?: boolean;
  standalone?: boolean;
  steps?: readonly string[];
  aliases?: readonly string[];
};
type NavItem = { key: string; icon: string; label: string; children?: NavChild[] };

// 地质力学工作台各模块 (来自 pages/geomechanics/modules), 归到「参数计算工作台」分组下。
// 开关关闭时必须一并隐藏: 路由此时会重定向回参数剖面, 菜单留着就是一排点不动的死项。
const MECHANICS_WORKBENCH_ITEMS: NavChild[] = (GEOMECHANICS_UI_ENABLED ? WORKBENCH_MODULES : [])
  .filter((module) => Number(module.key) >= 2)
  .map((module, i) => ({
    key: `/mechanics/workbench/${module.key}`,
    label: module.name,          // 不带 02/03 这类编号, 顺序本身已表达流程
    bullet: '▦',
    steps: module.steps,
    ...(i === 0 ? { group: '参数计算工作台' } : {}),
  }));

const NAV_ITEMS: NavItem[] = [
  { key: '/dashboard', icon: '◆', label: '仪表盘' },

  // ── 1 数据中心: 一切数据的入口与清单 (含标准化测井曲线) ──
  { key: '/datahub', icon: '▣', label: '数据中心', children: [
    { key: '/datahub/manage',  label: '数据清单', bullet: '▦' },
    { key: '/datahub/assets',  label: '数据审核', bullet: '↻', aliases: ['/datahub/process'] },
    { key: '/logging/curves',  label: '曲线视图', bullet: '∿' },
  ] },

  // ── 2 地质建模: 地层格架 + 天然裂缝 + 地震构造解释 ──
  { key: '/formation', icon: '▤', label: '地质建模', children: [
    { key: '/formation/layers',  label: '分层数据',  bullet: '▤', group: '地层格架' },
    { key: '/formation/profile', label: '2D 剖面',   bullet: '▥' },
    { key: '/formation/3d',      label: '3D 模型',   bullet: '◈' },
    { key: '/formation/tops',    label: '层位标记',  bullet: '·' },
    { key: '/naturalfrac/rose',    label: '走向玫瑰图', bullet: '✲', group: '天然裂缝' },
    { key: '/naturalfrac/density', label: '密度剖面',   bullet: '▥' },
    { key: '/naturalfrac/table',   label: '裂缝参数',   bullet: '▦' },
    { key: '/formation/seismic', label: '层位与断层', bullet: '◭', group: '构造解释' },
  ] },

  // ── 3 地质力学 ──
  { key: '/mechanics', icon: '●', label: '地质力学', children: [
    ...MECHANICS_WORKBENCH_ITEMS,
    // 工作台没开时不必套「成果回读」这层壳, 三个页面直接铺开
    { key: '/mechanics/profile', label: '参数剖面', bullet: '◧',
      ...(MECHANICS_WORKBENCH_ITEMS.length ? { group: '成果回读' } : {}) },
    { key: '/mechanics/stress',  label: '地应力',   bullet: '⊥' },
    { key: '/mechanics/rock-mechanics', label: '岩石力学参数', bullet: '●' },
  ] },

  // ── 4 井壁稳定 (依赖地应力与强度参数, 独立成模块) ──
  { key: '/stability', icon: '◎', label: '井壁稳定', children: [
    { key: '/stability/window',   label: '安全泥浆窗口', bullet: '◫' },
    { key: '/stability/pressure', label: '坍塌破裂压力', bullet: '⊥' },
    { key: '/stability/params',   label: '强度参数',     bullet: '▦' },
    { key: '/stability/imaging',  label: '成像测井解释', bullet: '▥' },
  ] },

  // ── 5 甜点评价 ──
  { key: '/sweetspot', icon: '◇', label: '甜点评价' },

  // ── 6 压裂设计 ──
  { key: '/fracture', icon: '⚡', label: '压裂设计', children: [
    { key: '/fracture/new-design', label: '新建方案', bullet: '+', group: '新建方案', groupLanding: true },
    { key: '/fracture/builder',   label: '分段分簇',   bullet: '⚡' },
    { key: '/fracture/wellbore',  label: '完井与射孔', bullet: '◉' },
    { key: '/fracture/materials', label: '材料设计',   bullet: '◈' },
    { key: '/fracture/schedule',  label: '泵注程序',   bullet: '~' },
    { key: '/fracture/designs',   label: '方案总览',   bullet: '※', standalone: true },
    { key: '/fracture/lab',       label: '物模实验库', bullet: '⚗', standalone: true },
  ] },

  // ── 7 压裂模拟 ──
  { key: '/fracsim', icon: '✸', label: '压裂模拟', children: [
    { key: '/fracsim/overview',     label: '三维总览',   bullet: '✸' },
    { key: '/fracsim/curves',       label: '动态曲线',   bullet: '∿' },
    { key: '/fracsim/history',      label: '方案对比',   bullet: '⟳' },
  ] },

  // ── 8 压中监测: 工况预警 + 自适应调控 (施工过程的"看"与"调") ──
  { key: '/warning', icon: '△', label: '压中监测', children: [
    { key: '/warning/runs',   label: '监测会话', bullet: '☰', group: '工况监测' },
    { key: '/warning/live',   label: '实时分析', bullet: '△' },
    { key: '/warning/events', label: '事件流',   bullet: '!' },
    { key: '/warning/rules',  label: '规则配置', bullet: '⚙' },
    { key: '/control/policy',   label: '策略演示', bullet: '⟲', group: '自适应调控' },
    { key: '/control/metrics',  label: '指标验证', bullet: '∑' },
    { key: '/control/training', label: '训练过程', bullet: '↗' },
  ] },

  // ── 9 裂缝反演 ──
  { key: '/inversion', icon: '✕', label: '裂缝反演', children: [
    { key: '/inversion/reconstruct', label: '缝网重构', bullet: '✕' },
    { key: '/inversion/stats',       label: '统计分析', bullet: '∑' },
  ] },

 // — 10 产能预测 —
  { key: '/production-forecast', icon: '▦', label: '产能预测' },

  // ── 11 经济评价 ──
  { key: '/economics', icon: '▦', label: '经济评价', children: [
    { key: '/economics/cost',       label: '成本构成', bullet: '¥' },
    { key: '/economics/production', label: '产量预测', bullet: '∿' },
    { key: '/economics/npv',        label: 'NPV/IRR',  bullet: '∑' },
  ] },
  
];

// 模块前缀匹配: /formation 命中 /formation/layers
const underPath = (base: string, pathname: string) =>
  pathname === base || pathname.startsWith(base + '/');

const isChildActive = (child: NavChild, pathname: string) =>
  underPath(child.key, pathname) || (child.aliases ?? []).some((alias) => underPath(alias, pathname));

// 模块可归并不同路径前缀的子页 (例: "地质建模" 含 /formation/* 与 /naturalfrac/*),
// 故激活判定除自身前缀外, 还要看任一子项前缀是否命中。
const isModuleActive = (item: NavItem, pathname: string) =>
  underPath(item.key, pathname) ||
  (item.children ?? []).some((c) => isChildActive(c, pathname));

/** 把子页按 group 归成「二级分组 → 三级页面」。无 group 的跟随上一个分组;
 *  整个模块都没有 group 时, 退化为单个匿名分组 (直接铺开页面, 不显示分组标题)。 */
type NavGroup = { name: string | null; items: NavChild[] };
function groupChildren(children: NavChild[]): NavGroup[] {
  const groups: NavGroup[] = [];
  for (const c of children) {
    if (c.group || c.standalone || groups.length === 0) groups.push({ name: c.group ?? null, items: [] });
    groups[groups.length - 1].items.push(c);
  }
  return groups;
}

const groupKey = (modKey: string, name: string) => `${modKey}|${name}`;

/** 当前路径命中的 模块 / 分组 / 三级页面, 用于路由变化时自动展开到对应位置。 */
type NavLoc = { modKey: string; gKey: string | null; pKey: string | null };
function locateByPath(pathname: string, items: NavItem[] = NAV_ITEMS): NavLoc | null {
  for (const item of items) {
    if (!isModuleActive(item, pathname)) continue;
    if (!item.children) return { modKey: item.key, gKey: null, pKey: null };
    for (const g of groupChildren(item.children)) {
      const hit = g.items.find((c) => isChildActive(c, pathname));
      if (hit) {
        return {
          modKey: item.key,
          gKey: g.name ? groupKey(item.key, g.name) : null,
          pKey: hit.steps?.length ? hit.key : null,
        };
      }
    }
    return { modKey: item.key, gKey: null, pKey: null };
  }
  return null;
}

export default function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, fetchProfile } = useAuthStore();
  const { currentProject, clearProject, validateCurrentProject } = useProjectStore();
  // 导航数据里存中文原文, 渲染时再翻译 —— navScope 白名单按 key 匹配, 不受语言影响
  const t = useT();

  // 受限账号 (甲方试用) 只看到白名单里的模块/页面; 白名单为空 = 自己人, 全量。
  // user 还没拉回来时先按全量渲染, profile 一到就收窄 —— 后端始终是拦得住的,
  // 这里早一帧晚一帧不影响安全。
  const navItems = React.useMemo(
    () => filterNav(NAV_ITEMS, user?.nav_whitelist),
    [user?.nav_whitelist],
  );

  // 展开状态由用户点击控制, 与路由解耦 —— 原来「展开=当前路由所在模块」,
  // 导致已激活的模块点了没反应、收不起来。初值只用来把当前所在位置先展开。
  const initLoc = locateByPath(location.pathname);
  const [openModules, setOpenModules] = useState<Record<string, boolean>>(
    () => (initLoc ? { [initLoc.modKey]: true } : {}));
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(
    () => (initLoc?.gKey ? { [initLoc.gKey]: true } : {}));
  // 第三级 (带 steps 的工作台页面) 同样归用户点击控制, 而不是"选中才露出"
  const [openPages, setOpenPages] = useState<Record<string, boolean>>(
    () => (initLoc?.pKey ? { [initLoc.pKey]: true } : {}));

  // 路由变化时自动展开到当前页所在的模块/分组/页面 (例如从别处跳转进来),
  // 只做"展开", 不做"收起", 免得和用户的手动折叠打架。
  useEffect(() => {
    const loc = locateByPath(location.pathname, navItems);
    if (!loc) return;
    setOpenModules((m) => (m[loc.modKey] ? m : { ...m, [loc.modKey]: true }));
    if (loc.gKey) setOpenGroups((g) => (g[loc.gKey!] ? g : { ...g, [loc.gKey!]: true }));
    if (loc.pKey) setOpenPages((p) => (p[loc.pKey!] ? p : { ...p, [loc.pKey!]: true }));
  }, [location.pathname]);

  useEffect(() => {
    if (!user) fetchProfile();
    // 缓存里的项目可能属于上一个登录的人 —— 校验一次, 不通过就回项目选择页
    validateCurrentProject().then((ok) => {
      if (!ok && location.pathname !== '/projects') navigate('/projects');
    });
  }, []);

  // If no project selected, redirect to project selection
  useEffect(() => {
    if (!currentProject && location.pathname !== '/projects') {
      navigate('/projects');
    }
  }, [currentProject, location.pathname]);

  return (
    <div className="app-layout">
      {/* Sidebar */}
      <aside className="sidebar">
          <div className="sidebar-header" style={{ position: 'relative' }}>
            <div className="logo">
              <span className="logo-icon">⬡</span>
              <div>
                <div className="logo-text">FracSight</div>
                <div className="logo-sub">{t('压裂智能平台')}</div>
              </div>
            </div>
            <LanguageSwitch tone="dark" style={{ position: 'absolute', top: 8, right: 8 }} />
          </div>

        {/* Current Project Indicator */}
        {currentProject && (
          <div className="sidebar-project">
            <div className="project-well">{currentProject.well_name}</div>
            <div className="project-name">{currentProject.name}</div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="project-switch" onClick={() => { clearProject(); navigate('/projects'); }}>
                {t('切换项目')}
              </button>
              <button className="project-switch" onClick={() => navigate('/datahub/import')}>
                {t('导入数据')}
              </button>
            </div>
          </div>
        )}

        <nav className="sidebar-nav">
          {navItems.map((item) => {
            const active = isModuleActive(item, location.pathname);
            const open = !!openModules[item.key];
            const groups = item.children ? groupChildren(item.children) : [];

            // 一级: 单击展开 / 再单击折叠。无子项的 (仪表盘) 直接跳转。
            const onModuleClick = () => {
              if (!item.children) { navigate(item.key); return; }
              setOpenModules((m) => ({ ...m, [item.key]: !m[item.key] }));
            };

            // 三级页面; 带 steps 的 (地质力学工作台) 与一二级一样单击展开 / 再单击折叠
            const renderPage = (c: NavChild) => {
              const sel = isChildActive(c, location.pathname);
              const hasSteps = !!c.steps?.length;
              const pOpen = !!openPages[c.key];
              const raw = Number(new URLSearchParams(location.search).get('step') ?? 0);
              const step = Number.isInteger(raw) && raw >= 0
                ? Math.min(raw, Math.max((c.steps?.length ?? 1) - 1, 0)) : 0;

              // 无步骤的页面: 点了就是跳转。有步骤的: 点了开合, 展开时顺带跳到第 1 步;
              // 收起时留在原页, 不做导航 —— 否则"折叠"会把人踢走。
              const onPageClick = () => {
                if (!hasSteps) { navigate(c.key); return; }
                const next = !pOpen;
                setOpenPages((p) => ({ ...p, [c.key]: next }));
                if (next && !sel) navigate(`${c.key}?step=0`);
              };

              return (
                <React.Fragment key={c.key}>
                  <button
                    className={`nav-subitem ${sel ? 'active' : ''} ${pOpen ? 'open' : ''}`}
                    onClick={onPageClick}
                    aria-expanded={hasSteps ? pOpen : undefined}
                  >
                    <span className="nav-subicon">{c.bullet ?? '·'}</span>
                    <span>{t(c.label)}</span>
                    {hasSteps && <span className="nav-subcaret">{pOpen ? '▾' : '▸'}</span>}
                  </button>
                  {hasSteps && pOpen && (
                    <div className="nav-steps">
                      {c.steps!.map((s, i) => (
                        <button
                          key={`${c.key}:${i}`}
                          className={`nav-stepitem ${sel && step === i ? 'active' : ''}`}
                          onClick={() => navigate(`${c.key}?step=${i}`)}
                        >
                          <span className="nav-stepindex">{i + 1}</span>
                          <span>{t(s)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              );
            };

            return (
              <React.Fragment key={item.key}>
                <button
                  className={`nav-item ${active ? 'active' : ''} ${open ? 'open' : ''}`}
                  onClick={onModuleClick}
                  aria-expanded={item.children ? open : undefined}
                >
                  <span className="nav-icon">{item.icon}</span>
                  <span className="nav-label">{t(item.label)}</span>
                  {item.children && <span className="nav-caret">{open ? '▾' : '▸'}</span>}
                  {active && <span className="nav-indicator" />}
                </button>

                {open && item.children && (
                  <div className="nav-children">
                    {groups.map((g, gi) => {
                      // 无分组名 → 直接铺开三级页面, 不占一层
                      if (!g.name) return <React.Fragment key={gi}>{g.items.map(renderPage)}</React.Fragment>;
                      const gk = groupKey(item.key, g.name);
                      const gOpen = !!openGroups[gk];
                      const landing = g.items.find((c) => c.groupLanding);
                      const childItems = g.items.filter((c) => !c.groupLanding);
                      const landingActive = landing ? isChildActive(landing, location.pathname) : false;
                      // 带落地页的分组本身就是一个页面, 与同级页面用同一套字号/缩进, 只多一个开合箭头
                      if (landing) return (
                        <React.Fragment key={gk}>
                          <button
                            className={`nav-subitem ${landingActive ? 'active' : ''} ${gOpen ? 'open' : ''}`}
                            onClick={() => {
                              setOpenGroups((x) => ({ ...x, [gk]: !x[gk] }));
                              navigate(landing.key);
                            }}
                            aria-expanded={gOpen}
                          >
                            <span className="nav-subicon">{landing.bullet ?? '·'}</span>
                            <span>{t(g.name)}</span>
                            {childItems.length > 0 && <span className="nav-subcaret">{gOpen ? '▾' : '▸'}</span>}
                          </button>
                          {gOpen && childItems.length > 0 && <div className="nav-grandchildren">{childItems.map(renderPage)}</div>}
                        </React.Fragment>
                      );
                      return (
                        <React.Fragment key={gk}>
                          {/* 二级: 同样单击展开 / 再单击折叠 */}
                          <button
                            className={`nav-group ${gOpen ? 'open' : ''}`}
                            onClick={() => setOpenGroups((x) => ({ ...x, [gk]: !x[gk] }))}
                            aria-expanded={gOpen}
                          >
                            <span className="nav-group-caret">{gOpen ? '▾' : '▸'}</span>
                            <span>{t(g.name)}</span>
                          </button>
                          {gOpen && <div className="nav-grandchildren">{g.items.map(renderPage)}</div>}
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </nav>

          <div className="sidebar-footer">
            <div className="user-info">
              <div className="user-avatar">
                {user?.username?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="user-details">
                <div className="user-name">{user?.username || t('未登录')}</div>
                <div className="user-role">{user?.company || ''}</div>
              </div>
            </div>
            <button className="logout-btn" onClick={logout}>{t('退出')}</button>
          </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        {isPathAllowed(navItems, location.pathname, user?.nav_whitelist)
          ? <Outlet />
          : <ModuleNotEnabled />}
      </main>

      {/* 全局 AI 顾问 — 任何业务页右下角悬浮按钮 */}
      <AIPanel />
    </div>
  );
}

/** 直接敲 URL 进到未开通模块时的落地页。
 *  给一句明确的话, 而不是一个半截渲染的空页面 —— 后者会被当成"系统坏了"。 */
function ModuleNotEnabled() {
  const navigate = useNavigate();
  const t = useT();
  return (
    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', maxWidth: 420 }}>
        <div style={{ fontSize: 32, color: '#8598AD', marginBottom: 12 }}>◔</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#1E2A3A', marginBottom: 8 }}>
          {t('该模块尚未对当前账号开通')}
        </div>
        <div style={{ fontSize: 13, color: '#4F6680', lineHeight: 1.7, marginBottom: 20 }}>
          {t('当前账号的可见范围由管理员配置。需要开通请联系平台管理员。')}
        </div>
        <button
          onClick={() => navigate('/dashboard')}
          style={{
            fontSize: 13, padding: '7px 18px', borderRadius: 4, cursor: 'pointer',
            border: '1px solid #C2533A', background: '#C2533A', color: '#fff',
          }}
        >
          {t('回到仪表盘')}
        </button>
      </div>
    </div>
  );
}
